// supervisor-pause-resume-directive-step.ts
//
// Split out of supervisor-halt-directive-step.ts (D12, phase 05 B1 — that
// file sat at the 199-line budget ceiling). Owns the `pause`/`resume`
// directive handling ONLY; abort/abort_confirm stay in the halt-step file.
//
// Phase 05 Req 6: pause/resume now journal (`mission_pause_applied` /
// `mission_resume_applied`, each carrying `directive_id`) in addition to
// applying `requestPause`/`resumeFromPause` — before this phase they only
// wrote pause-state.json with NO journal trace at all (Key Insight B-pause).
// Idempotent via `operation_id` = `pause-apply-<directive_id>` /
// `resume-apply-<directive_id>` (same mechanism directive-redirect-
// application.ts uses): check `store.state.results[opId]` BEFORE
// committing, so a crash between commit and `markDirectiveProcessed` never
// journals twice on replay (B14 crash window).
import { randomUUID } from "node:crypto";
import { markDirectiveProcessed } from "./directive-inbox.js";
import type { MissionStore } from "./mission-store.js";
import { requestPause, resumeFromPause } from "./pause-resume-state.js";
import { unblockQuotaMisclassifiedTickets } from "./resume-unblock-quota-misclassified.js";
import type { Directive } from "./schemas/directive-schema.js";

function pauseOperationId(directiveId: string): string {
  return `pause-apply-${directiveId}`;
}

function resumeOperationId(directiveId: string): string {
  return `resume-apply-${directiveId}`;
}

/**
 * Applies one `pause` or `resume` directive: journals the durable event
 * (unless already committed — replay safety), applies the actual
 * pause-state.json change, then marks the directive processed. This exact
 * order (i → ii → iii) is what makes the crash window safe (B14): a crash
 * between (i) and (iii) leaves the directive pending, and the NEXT pass's
 * (i) sees `results[opId]` already set, so it skips straight to
 * re-applying (ii) and marking (iii) — never a second journal event.
 */
export async function applyPauseOrResumeDirective(
  store: MissionStore,
  controlRoot: string,
  directive: Directive,
  notify?: (line: string) => void,
): Promise<void> {
  const isPause = directive.kind === "pause";
  const operationId = isPause
    ? pauseOperationId(directive.directive_id)
    : resumeOperationId(directive.directive_id);

  if (store.state.results[operationId] === undefined) {
    await store.commit([
      {
        event_id: `${operationId}-intent`,
        operation_id: operationId,
        delta: {
          kind: "operation_intent",
          operation_id: operationId,
          operation_kind: isPause ? "pause_apply" : "resume_apply",
          params: { directive_id: directive.directive_id },
        },
      },
      {
        event_id: `${operationId}-result`,
        operation_id: operationId,
        delta: { kind: "operation_result", operation_id: operationId, outcome: "applied" },
      },
      {
        event_id: randomUUID(),
        // Phase 05 H3: NOT `mission_status_changed` — that kind overwrites
        // `state.status` (mission-state-reducer.ts's own sticky-ABORTED
        // case), which would clobber the mission's pre-pause label and
        // leave resume with nothing to restore. These 2 kinds fall through
        // the reducer's `default: break` (same as `directive_rejected`),
        // same as mission-delta-kinds.ts documents.
        delta: {
          kind: isPause ? "mission_pause_applied" : "mission_resume_applied",
          directive_id: directive.directive_id,
        } as never,
      },
    ]);
  }

  if (isPause) await requestPause(controlRoot, [], [], null);
  else {
    await resumeFromPause(controlRoot);
    // 06g R8b: a ticket blocked only by quota-worded failures goes back to todo.
    await unblockQuotaMisclassifiedTickets(store, controlRoot, notify);
  }

  await markDirectiveProcessed(controlRoot, directive.directive_id);
}
